import { differenceInCalendarDays } from 'date-fns';
import { ClassDiagnosticStatusReport } from './chartHelper';

/**
 * The two time-based prompts shown next to the diagnostic widget. Both nudge about a
 * diagnostic that has been running a while without every student finishing it; the
 * 30-day one also unlocks the "hide this widget" action.
 */
export enum DiagnosticNudge {
  TWO_WEEK = 'two-week',
  THIRTY_DAY = 'thirty-day',
}

/** Days since the *first* student started before each nudge fires. */
const THRESHOLD_DAYS: Record<DiagnosticNudge, number> = {
  [DiagnosticNudge.TWO_WEEK]: 14,
  [DiagnosticNudge.THIRTY_DAY]: 30,
};

/** How long a nudge stays on screen before it dismisses itself. */
export const NUDGE_LIFETIME_MS = 10_000;

export const NUDGE_TEXT: Record<DiagnosticNudge, string> = {
  [DiagnosticNudge.TWO_WEEK]:
    "It's been 2 weeks since your class started the Diagnostic. Check the pie chart to see who could use a nudge!",
  [DiagnosticNudge.THIRTY_DAY]:
    "It's been a month on the Diagnostic. Do students need help or are you ready to hide this widget?",
};

const STORAGE_KEY = 'diagnosticWidgetNudgesSeen';

/**
 * Whole days since the first student in the report started, or null when nobody has.
 * `sessionStartedOn` is non-null in the schema, so rows that never really started can
 * carry a placeholder date — the year check drops those along with unparseable ones.
 */
export const getDaysSinceDiagnosticStart = (diagnosticResults: ClassDiagnosticStatusReport): number | null => {
  const [firstStart] = (diagnosticResults ?? [])
    .flatMap(report => report.student.diagnostics ?? [])
    .map(diagnostic => new Date(diagnostic?.sessionStartedOn ?? NaN))
    .filter(startedOn => startedOn.getFullYear() > 2000)
    .sort((a, b) => a.getTime() - b.getTime());

  return firstStart ? differenceInCalendarDays(new Date(), firstStart) : null;
};

/** Which nudge the elapsed time qualifies for. The 30-day one wins when both apply. */
export const resolveCandidateNudge = (daysSinceStart: number | null, isSiteAdmin: boolean): DiagnosticNudge | null => {
  if (daysSinceStart === null) return null;
  if (daysSinceStart >= THRESHOLD_DAYS[DiagnosticNudge.THIRTY_DAY]) return DiagnosticNudge.THIRTY_DAY;
  // The 2-week nudge is teacher-only.
  if (!isSiteAdmin && daysSinceStart >= THRESHOLD_DAYS[DiagnosticNudge.TWO_WEEK]) return DiagnosticNudge.TWO_WEEK;

  return null;
};

/**
 * The 30-day mark is what lets a still-unfinished widget be hidden. It stays true after
 * the bubble is gone, so it keys off the elapsed time rather than off the active nudge.
 */
export const canHideUnfinishedDiagnostic = (daysSinceStart: number | null): boolean =>
  daysSinceStart !== null && daysSinceStart >= THRESHOLD_DAYS[DiagnosticNudge.THIRTY_DAY];

const buildNudgeId = (nudge: DiagnosticNudge, userId: string, classId: string) => `${nudge}|${userId}|${classId}`;

const readSeenNudges = (): string[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to parse seen diagnostic nudges from localStorage', error);
    localStorage.removeItem(STORAGE_KEY);
    return [];
  }
};

export const hasSeenNudge = (nudge: DiagnosticNudge, userId: string, classId: string): boolean =>
  readSeenNudges().includes(buildNudgeId(nudge, userId, classId));

/**
 * Each nudge shows at most once per user-class, and is recorded as soon as it goes on
 * screen rather than when it closes — a reload during its 10s life would bring it back.
 */
export const markNudgeSeen = (nudge: DiagnosticNudge, userId: string, classId: string): void => {
  const seen = readSeenNudges();
  const id = buildNudgeId(nudge, userId, classId);

  if (seen.includes(id)) return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen, id]));
  } catch (error) {
    console.error('Failed to persist seen diagnostic nudge to localStorage', error);
  }
};
